/**
 * Selector de cliente o centro.
 *
 * Sustituye a los campos donde había que pegar un UUID. Buscar el
 * identificador en otra pantalla, copiarlo y pegarlo no es sólo incómodo:
 * es la forma más fiable de acabar creando un equipo en el centro
 * equivocado, porque un UUID mal pegado se ve exactamente igual que uno
 * bien pegado.
 *
 * Usa la misma búsqueda que ⌘K, así que hereda su filtro por permisos y
 * por ámbito: **no se puede elegir lo que no se puede ver**.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useSession } from '../session/session-context.js';

interface SearchHit {
  type: string;
  id: string;
  title: string;
  subtitle: string | null;
  code: string;
}

interface Props {
  label: string;
  /** Qué se busca: `CUSTOMER` o `SITE`. */
  entity: 'CUSTOMER' | 'SITE';
  value: string;
  onChange: (id: string) => void;
  /** Nombre ya conocido, para pintar la selección al abrir en edición. */
  initialLabel?: string | undefined;
  error?: string | undefined;
  help?: string;
  required?: boolean;
  disabled?: boolean;
}

export function EntityPicker({
  label,
  entity,
  value,
  onChange,
  initialLabel,
  error,
  help,
  required,
  disabled,
}: Props): React.ReactNode {
  const { api } = useSession();
  const id = useId();

  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [chosen, setChosen] = useState<string | null>(initialLabel ?? null);
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const request = useRef(0);

  useEffect(() => {
    if (initialLabel !== undefined) setChosen(initialLabel);
  }, [initialLabel]);

  useEffect(() => {
    if (!open) return;

    const onPointer = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setHits([]);
      return;
    }

    const ticket = ++request.current;
    const timer = setTimeout(() => {
      api
        .get<{ results: SearchHit[] }>(`/search?q=${encodeURIComponent(term)}&limit=10`)
        .then((data) => {
          if (ticket !== request.current) return;
          setHits((data.results ?? []).filter((hit) => hit.type === entity));
        })
        .catch(() => {
          if (ticket === request.current) setHits([]);
        });
    }, 180);

    return () => clearTimeout(timer);
  }, [api, query, entity]);

  function choose(hit: SearchHit): void {
    onChange(hit.id);
    setChosen(`${hit.title}${hit.code && hit.code !== hit.title ? ` · ${hit.code}` : ''}`);
    setOpen(false);
    setQuery('');
  }

  function clear(): void {
    onChange('');
    setChosen(null);
    setQuery('');
  }

  return (
    <div className={`field${error ? ' field--error' : ''}`} ref={container}>
      <label htmlFor={id}>
        {label}
        {!required && <span className="field__optional">opcional</span>}
      </label>

      {value !== '' && chosen !== null ? (
        <div className="picker__chosen">
          <span>{chosen}</span>
          {!disabled && (
            <button type="button" className="table__link" onClick={clear}>
              Cambiar
            </button>
          )}
        </div>
      ) : (
        <div className="picker">
          <input
            id={id}
            type="search"
            value={query}
            disabled={disabled ?? false}
            placeholder={entity === 'CUSTOMER' ? 'Buscar cliente…' : 'Buscar centro…'}
            aria-invalid={error ? true : undefined}
            aria-autocomplete="list"
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
          />

          {open && query.trim().length >= 2 && (
            <ul className="picker__list" role="listbox">
              {hits.map((hit) => (
                <li key={hit.id}>
                  <button type="button" className="picker__item" onClick={() => choose(hit)}>
                    <span>{hit.title}</span>
                    <span className="palette__hint">{hit.subtitle ?? hit.code}</span>
                  </button>
                </li>
              ))}
              {hits.length === 0 && (
                <li className="picker__empty">
                  Nada coincide. Sólo aparece lo que su perfil puede ver.
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      {help && <p className="field__help">{help}</p>}
      {error && (
        <p className="field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
